import * as Sentry from "@sentry/browser";

import {notify} from "@client/features/notifications";
import {loadSearch} from "@client/features/search";

import * as analytics from "@client/shared/analytics";

import {getTag} from "@client/hardware";
import * as forms from "@client/site/forms";
import type {Action, AppDispatch} from "@client/store";
import {server} from "@reactivated";

export function useLoginForm() {
    const form = forms.useAutoPform(server.core.rpc.Login);

    const processErrors = (errors: {loc: string[]; msg: string}[]) => {
        const errorsMap = Object.fromEntries(
            errors.filter((e) => e.loc.length == 1).map((e) => [e.loc[0], [e.msg]]),
        );
        form.setErrors(errorsMap);
    };

    return {
        ...form,
        processErrors,
    };
}

export function useRegistrationForm() {
    const form = forms.useAutoPform(server.core.rpc.Register);

    const processErrors = (errors: {loc: string[]; msg: string}[]) => {
        const errorsMap = Object.fromEntries(
            errors.filter((e) => e.loc.length == 1).map((e) => [e.loc[0], [e.msg]]),
        );
        form.setErrors(errorsMap);
    };

    return {
        ...form,
        processErrors,
    };
}

export const logout = () => async (dispatch: AppDispatch) => {
    const result = await server.core.rpc.logout();

    if (result.type === "success") {
        // Currently fails without the timeout, goes into a blank screen.
        setTimeout(() => {
            dispatch({
                type: "LOGOUT",
            });
        }, 500);

        Sentry.setUser(null);
        analytics.logout("app");

        dispatch(notify("You have been logged out of your account."));
        const router = await import("@client/router");
        router.push("login.index", {});
    }
};

export type Actions = {
    type: "LOGOUT";
};

const initialState = {};

export const reducer = (state = initialState, action: Action) => {
    switch (action.type) {
        case "LOGOUT":
            return initialState;
        default: {
            return state;
        }
    }
};

export const postLogin =
    (profile: server.core.rpc.Profile.output, newlyLoggedIn: boolean) =>
    async (dispatch: AppDispatch) => {
        dispatch({
            type: "SET_PROFILE",
            profile,
        });

        analytics.identify({
            tracking_key: profile.user.tracking_key,
            should_collect_analytics: profile.user.should_collect_analytics,
        });
        if (newlyLoggedIn === true) {
            void analytics.track("LOGIN");
        }
        const joyTracking = await getTag();

        Sentry.setUser({
            joy_tag: joyTracking,
            email: profile.user.email,
            id: profile.user.id.toString(),
            has_beta_access: profile.user.has_beta_access,
        });

        // We've set this up in the backend, but want client-side properties too.
        // Also this ensures we have profiles for all users in the old mixpanel
        // project.
        analytics.people.set({
            $email: profile.user.email,
            has_hidden_macros: profile.has_hidden_macros,
            dismissed_announcements: profile.user.casted_dismissed_announcements,
        });

        void dispatch(loadSearch({reset: newlyLoggedIn}));
    };

export const redirectToLogin = () => async (dispatch: AppDispatch) => {
    dispatch({
        type: "LOGOUT",
    });
    // Currently we have no way of knowing a first time user from someone who
    // Lost their session. We'd need to keep track of login state.
    // dispatch(notify("Your session has expired. Please login again.", "error"));
    const router = await import("@client/router");
    router.push("login.index", {});
};
